// Shell-wide notification state: whether the drawer is open (the bell, `g n` and the palette all
// toggle it), notifications hidden while their dismissal waits out its undo window, and briefs
// whose banner the reader waved away this session.
import { useSyncExternalStore } from "react";

let open = false;
let hidden = new Set<string>();
let dismissedBriefs = new Set<string>(readDismissed());
let version = 0;
const listeners = new Set<() => void>();

const BRIEF_KEY = "mm-brief-banner-dismissed";

function readDismissed(): string[] {
  try {
    const raw = typeof sessionStorage === "undefined" ? null : sessionStorage.getItem(BRIEF_KEY);
    const value: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

function emit() {
  version++;
  for (const l of listeners) l();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const notifyStore = {
  setOpen(next: boolean) {
    if (open === next) return;
    open = next;
    emit();
  },
  toggle() {
    open = !open;
    emit();
  },
  hide(id: string) {
    hidden = new Set(hidden).add(id);
    emit();
  },
  unhide(id: string) {
    hidden = new Set(hidden);
    hidden.delete(id);
    emit();
  },
  dismissBrief(id: string) {
    dismissedBriefs = new Set(dismissedBriefs).add(id);
    try {
      sessionStorage.setItem(BRIEF_KEY, JSON.stringify([...dismissedBriefs].slice(-20)));
    } catch {
      // storage unavailable: the banner may come back after a reload, which is harmless
    }
    emit();
  },
};

function useVersion() {
  return useSyncExternalStore(
    subscribe,
    () => version,
    () => version,
  );
}

export function useDrawerOpen(): boolean {
  useVersion();
  return open;
}

export function useHiddenNotifications(): ReadonlySet<string> {
  useVersion();
  return hidden;
}

export function useDismissedBriefs(): ReadonlySet<string> {
  useVersion();
  return dismissedBriefs;
}
